import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Collapse,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PublishIcon from '@mui/icons-material/Publish';
import { apiService } from '../../services/api';
import type { ToastState } from './types';

interface PromotionControlsCardProps {
  promoteSchemaId: string;
  setPromoteSchemaId: (value: string) => void;
  promoteSchemaName: string;
  setPromoteSchemaName: (value: string) => void;
  promoteEnvironment: string;
  setPromoteEnvironment: (value: string) => void;
  isPromoting: boolean;
  isApproved: boolean;
  onPromote: () => void;
  setToast: (value: ToastState | null) => void;
}

const PromotionControlsCard: React.FC<PromotionControlsCardProps> = ({
  promoteSchemaId,
  setPromoteSchemaId,
  promoteSchemaName,
  setPromoteSchemaName,
  promoteEnvironment,
  setPromoteEnvironment,
  isPromoting,
  isApproved,
  onPromote,
  setToast,
}) => {
  const [showCreateSetInline, setShowCreateSetInline] = useState(false);
  const [newSetNameInline, setNewSetNameInline] = useState('');
  const [creatingSetInline, setCreatingSetInline] = useState(false);

  const handleCreateSet = async () => {
    setCreatingSetInline(true);
    try {
      const newSet = await apiService.createSchema({
        schemaName: newSetNameInline.trim(),
        status: 'active',
        createdBy: 'web-ui',
      });
      setPromoteSchemaId(newSet.schema_id);
      setShowCreateSetInline(false);
      setNewSetNameInline('');
      setToast({ severity: 'success', message: `Schema "${newSet.schema_name}" created.` });
    } catch (err) {
      setToast({
        severity: 'error',
        message: err instanceof Error ? err.message : 'Failed to create schema.',
      });
    } finally {
      setCreatingSetInline(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Promotion Controls
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Schema ID"
            value={promoteSchemaId}
            onChange={(event) => setPromoteSchemaId(event.target.value)}
            helperText="Leave blank to use the draft's linked schema."
            size="small"
          />
          <Button
            variant="text"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateSetInline((prev) => !prev)}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {showCreateSetInline ? 'Cancel new set' : 'or create a new set'}
          </Button>
          <Collapse in={showCreateSetInline}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="New Set Name"
                value={newSetNameInline}
                onChange={(event) => setNewSetNameInline(event.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={creatingSetInline || !newSetNameInline.trim()}
                onClick={handleCreateSet}
                sx={{ mt: '4px' }}
              >
                {creatingSetInline ? 'Creating\u2026' : 'Create'}
              </Button>
            </Stack>
          </Collapse>
          <TextField
            label="Schema Name"
            value={promoteSchemaName}
            onChange={(event) => setPromoteSchemaName(event.target.value)}
            helperText="Optional override for resulting schema name."
            size="small"
          />
          <TextField
            label="Environment"
            value={promoteEnvironment}
            onChange={(event) => setPromoteEnvironment(event.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={onPromote}
            disabled={isPromoting || isApproved}
          >
            {isPromoting ? 'Promoting\u2026' : 'Promote Draft'}
          </Button>
          {isApproved && (
            <Alert severity="success">
              Draft already promoted. Further edits require new version.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PromotionControlsCard;
